import {useState} from "react"
import {useTranslation} from "react-i18next"
import {Link} from "react-router-dom"
import {Heading} from "@/components/tailwind/heading"
import {Input} from "@/components/tailwind/input"
import {Button} from "@/components/tailwind/button"
import {Text} from "@/components/tailwind/text"
import {Divider} from "@/components/tailwind/divider"
import {requestPasswordReset} from "@/services/password-reset-services"
import ValidationAlert from "@/components/validation-alert"

export default function PasswordResetRequestPage() {
    const {t} = useTranslation("login")
    const [username, setUsername] = useState("")
    const [isLoading, setIsLoading] = useState(false)
    const [isSubmitted, setIsSubmitted] = useState(false)
    const [error, setError] = useState<string | null>(null)

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault()
        setError(null)

        if (!username.trim()) {
            setError(t("forgotPassword.errors.usernameEmpty"))
            return
        }

        setIsLoading(true)
        try {
            await requestPasswordReset(username.trim())
            setIsSubmitted(true)
        } catch (_e) {
            setError(t("forgotPassword.errors.requestFailed"))
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div className="mx-auto max-w-4xl">
            <Heading level={2}>{t("forgotPassword.headline")}</Heading>
            <Divider className="my-10 mt-6"/>

            {isSubmitted ? (
                <div data-testid="password-reset-success">
                    <div className="bg-blue-50 dark:bg-blue-950 border-l-4 border-blue-400 dark:border-blue-500 p-4 mb-6">
                        <Text className="text-blue-700 dark:text-blue-300">
                            {t("forgotPassword.successMessage")}
                        </Text>
                    </div>
                    <Link
                        to="/login"
                        className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                    >
                        {t("forgotPassword.backToLogin")}
                    </Link>
                </div>
            ) : (
                <form onSubmit={handleSubmit}>
                    <Text className="mb-6">{t("forgotPassword.description")}</Text>

                    {error && (
                        <section className="mb-4">
                            <ValidationAlert message={error}/>
                        </section>
                    )}

                    <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
                        <div className="space-y-1">
                            <Text>{t("forgotPassword.form.username.label")}</Text>
                        </div>
                        <div>
                            <Input
                                type="text"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                placeholder={t("forgotPassword.form.username.placeholder")}
                                aria-label={t("forgotPassword.form.username.ariaLabel")}
                                disabled={isLoading}
                                data-testid="password-reset-username"
                            />
                        </div>
                    </section>

                    <section className="flex justify-between items-center mt-6">
                        <Link
                            to="/login"
                            className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                        >
                            {t("forgotPassword.backToLogin")}
                        </Link>
                        <Button
                            type="submit"
                            disabled={isLoading}
                            data-testid="password-reset-submit"
                        >
                            {isLoading ? t("forgotPassword.form.submit.loading") : t("forgotPassword.form.submit.label")}
                        </Button>
                    </section>
                </form>
            )}
        </div>
    )
}
